import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function PdfReorder() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [result, setResult] = useState(null);

  async function loadThumbs(f) {
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/pdf/thumbnails", fd);
      setPages(data.pages);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function onDragStart(i) {
    setDragIdx(i);
  }
  function onDragOver(e, i) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    setPages((prev) => {
      const next = [...prev];
      const [m] = next.splice(dragIdx, 1);
      next.splice(i, 0, m);
      return next;
    });
    setDragIdx(i);
  }
  function onDragEnd() {
    setDragIdx(null);
  }

  async function onSubmit() {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("order", pages.map((p) => p.page).join(","));
      const { data } = await api.post("/pdf/reorder", fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-reorder-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_reorder")}</h1>
      <p className="text-zinc-500 mt-2">{t("pdf_drag_to_reorder")}</p>

      <div className="mt-8">
        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => { setFile(fs[0]); loadThumbs(fs[0]); }} testId="reorder-dropzone" />
        ) : (
          <>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <span className="eyebrow">{file.name}</span>
              <div className="flex gap-3">
                <Button variant="ghost" className="rounded-sm" onClick={() => { setFile(null); setPages([]); setResult(null); }} data-testid="reorder-clear">
                  {t("cta_clear")}
                </Button>
                <Button onClick={onSubmit} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="reorder-submit">
                  {t("cta_apply")} →
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="reorder-pages-grid">
              {pages.map((p, i) => (
                <div
                  key={p.page}
                  draggable
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(e, i)}
                  onDragEnd={onDragEnd}
                  className="page-tile p-2 rounded-md flex flex-col items-center cursor-move"
                  data-testid={`reorder-tile-${p.page}`}
                >
                  <img src={p.data_url} alt={`Page ${p.page}`} className="w-full h-auto" />
                  <div className="mt-2 flex justify-between w-full px-1">
                    <span className="eyebrow">{i + 1}</span>
                    <span className="eyebrow text-zinc-400">P{p.page}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="reorder-result">
            <span className="eyebrow">{t("success")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <div className="mt-4">
              <Button onClick={() => downloadFile(result.id, result.original_filename)}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10" data-testid="reorder-download">
                ↓ {t("cta_download")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
